import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { posApi } from '@/services/api';
import { Landmark, Plus, Edit2, Trash2, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { useEscapeToClose } from '@/hooks/useEscapeToClose';

interface CashPocket {
  name: string;
  pocket_name: string;
  opening_balance: number;
  current_balance: number;
  is_default: number;
}

export default function CashPocketsTab() {
  const qc = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPocket, setEditingPocket] = useState<CashPocket | null>(null);
  
  const [form, setForm] = useState({
    pocket_name: '',
    opening_balance: 0,
    is_default: false
  });
  
  const [errorMsg, setErrorMsg] = useState('');
  useEscapeToClose(() => setIsModalOpen(false), isModalOpen);

  const { data: pockets = [], isLoading } = useQuery({
    queryKey: ['cashPockets'],
    queryFn: async () => {
      const res = await posApi.getCashPockets();
      return res.data.message as CashPocket[];
    }
  });

  const createMutation = useMutation({
    mutationFn: (data: { pocket_name: string; opening_balance: number; is_default: boolean }) =>
      posApi.createCashPocket(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cashPockets'] });
      setIsModalOpen(false);
      resetForm();
    },
    onError: (err: any) => {
      setErrorMsg(err.response?.data?.message || err.message || 'Failed to create cash pocket');
    }
  });

  const updateMutation = useMutation({
    mutationFn: (data: { name: string; pocket_name: string; is_default: boolean }) =>
      posApi.updateCashPocket(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cashPockets'] });
      setIsModalOpen(false);
      resetForm();
    },
    onError: (err: any) => {
      setErrorMsg(err.response?.data?.message || err.message || 'Failed to update cash pocket');
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (name: string) => posApi.deleteCashPocket(name),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cashPockets'] });
      setDeletePocketName(null);
    },
    onError: (err: any) => {
      setErrorMsg(err.response?.data?.message || err.message || 'Cannot delete cash pocket. It might be linked to existing transactions.');
    }
  });

  const resetForm = () => {
    setForm({ pocket_name: '', opening_balance: 0, is_default: false });
    setEditingPocket(null);
    setErrorMsg('');
  };

  const handleOpenAdd = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const handleOpenEdit = (pocket: CashPocket) => {
    setEditingPocket(pocket);
    setForm({
      pocket_name: pocket.pocket_name,
      opening_balance: pocket.opening_balance,
      is_default: pocket.is_default === 1
    });
    setIsModalOpen(true);
  };

  const [deletePocketName, setDeletePocketName] = useState<string | null>(null);

  const handleDeleteClick = (name: string) => {
    setDeletePocketName(name);
  };

  const confirmDelete = () => {
    if (deletePocketName) {
      deleteMutation.mutate(deletePocketName);
      setDeletePocketName(null);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg('');
    if (!form.pocket_name.trim()) {
      setErrorMsg('Pocket name is required');
      return;
    }
    
    if (editingPocket) {
      updateMutation.mutate({
        name: editingPocket.name,
        pocket_name: form.pocket_name,
        is_default: form.is_default
      });
    } else {
      createMutation.mutate({
        pocket_name: form.pocket_name,
        opening_balance: form.opening_balance,
        is_default: form.is_default
      });
    }
  };

  return (
    <div className="space-y-4">
      <div className="card p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2">
              <Landmark className="w-5 h-5 text-primary-600" />
              Cash Pockets
            </h2>
            <p className="text-sm text-gray-500">Manage your store's cash drawers and pockets.</p>
          </div>
          <button onClick={handleOpenAdd} className="btn-primary flex items-center gap-2">
            <Plus className="w-4 h-4" /> Add Cash Pocket
          </button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12 text-gray-500">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : pockets.length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-xl">
            <Landmark className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No cash pockets found. Add your first one!</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <th className="p-4 rounded-tl-xl">Pocket Name</th>
                  <th className="p-4 text-right rounded-tr-xl">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pockets.map((pocket) => (
                  <tr key={pocket.name} className="hover:bg-gray-50/50 transition-colors">
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{pocket.pocket_name}</span>
                        {pocket.is_default === 1 && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-primary-50 text-primary-700">
                            <CheckCircle className="w-3 h-3" /> Default
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleOpenEdit(pocket)}
                          className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(pocket.name)}
                          disabled={deleteMutation.isPending}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                          title="Delete"
                        >
                          {deleteMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingPocket ? 'Edit Cash Pocket' : 'Add Cash Pocket'}
              </h3>
              <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {errorMsg && (
                <div className="p-3 bg-red-50 border border-red-100 text-red-600 text-sm rounded-lg flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <p>{errorMsg}</p>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Pocket Name *</label>
                <input
                  type="text"
                  className="input-field"
                  placeholder="e.g. Main Drawer"
                  value={form.pocket_name}
                  onChange={e => setForm({ ...form, pocket_name: e.target.value })}
                  autoFocus
                />
              </div>

              {!editingPocket && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Opening Balance</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="input-field font-mono"
                    value={form.opening_balance}
                    onChange={e => setForm({ ...form, opening_balance: parseFloat(e.target.value) || 0 })}
                  />
                  <p className="text-xs text-gray-500 mt-1">Cannot be changed later.</p>
                </div>
              )}

              <label className="flex items-center gap-3 pt-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_default}
                  onChange={e => setForm({ ...form, is_default: e.target.checked })}
                  className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                />
                <span className="text-sm font-medium text-gray-700">Set as default cash pocket</span>
              </label>

              <div className="pt-4 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="btn-primary flex items-center gap-2"
                >
                  {(createMutation.isPending || updateMutation.isPending) && <Loader2 className="w-4 h-4 animate-spin" />}
                  {editingPocket ? 'Save Changes' : 'Create Cash Pocket'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deletePocketName && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center shrink-0">
                <AlertCircle className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Cash Pocket</h3>
            </div>
            
            <div className="p-6">
              {errorMsg ? (
                <div className="p-4 bg-red-50 text-red-700 rounded-lg text-sm mb-4">
                  {errorMsg}
                </div>
              ) : (
                <p className="text-gray-600 mb-6">
                  Are you sure you want to delete this cash pocket? This action cannot be undone.
                </p>
              )}
              
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => { setDeletePocketName(null); setErrorMsg(''); }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  {errorMsg ? 'Close' : 'Cancel'}
                </button>
                {!errorMsg && (
                  <button
                    onClick={confirmDelete}
                    disabled={deleteMutation.isPending}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
                  >
                    {deleteMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                    Delete
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
